import { useEffect, useMemo, useState } from 'react'
import {
  ArchiveRestore,
  CheckCircle2,
  Loader2,
  RotateCcw,
  Save,
  Send,
  Trash2,
  Undo2,
  X,
  XCircle,
} from 'lucide-react'
import { toast } from 'sonner'
import { Button, Chip, Drawer, EmptyState } from '@/components/ui'
import { ApiError } from '@/lib/api/jsonFetch'
import { SourcingPanel } from '@/shell/services/vendor-alternates-resolver/components/SourcingPanel'
import {
  useCreateFormulation,
  useDeleteFormulation,
  useFormulation,
  useRestoreFormulation,
  useTransitionFormulation,
  useUpdateFormulation,
} from '../api/queries'
import type {
  Formulation,
  FormulationStatus,
  FormulationWrite,
  Ingredient,
} from '../api/types'
import { HistoryPanel } from './HistoryPanel'
import { IngredientsGrid } from './IngredientsGrid'
import { ProcedureEditor } from './ProcedureEditor'
import { StatusChip } from './StatusChip'
import { TransitionDialog } from './TransitionDialog'

/** Sprint 08b — contextual transition button set per current status.
 *  Mirrors the spec §Item 5 grid. */
interface TransitionAction {
  to: FormulationStatus
  label: string
  variant: 'primary' | 'secondary' | 'danger'
  icon: typeof CheckCircle2
}

function transitionsFor(status: FormulationStatus): TransitionAction[] {
  switch (status) {
    case 'draft':
      return [
        { to: 'testing', label: 'Submit for testing', variant: 'primary', icon: Send },
        { to: 'rejected', label: 'Reject', variant: 'danger', icon: XCircle },
      ]
    case 'testing':
      // Approve uses primary variant — Button primitive doesn't carry a
      // `success` variant; the StatusChip carries the green tone instead.
      return [
        { to: 'approved', label: 'Approve', variant: 'primary', icon: CheckCircle2 },
        { to: 'rejected', label: 'Reject', variant: 'danger', icon: XCircle },
        { to: 'draft', label: 'Back to draft', variant: 'secondary', icon: RotateCcw },
      ]
    case 'approved':
      return [
        { to: 'draft', label: 'Back to draft', variant: 'secondary', icon: RotateCcw },
        { to: 'rejected', label: 'Reject', variant: 'danger', icon: XCircle },
      ]
    case 'rejected':
      return [
        { to: 'draft', label: 'Revive to draft', variant: 'secondary', icon: RotateCcw },
      ]
  }
}

/**
 * Detail pane for a selected formulation. Narrow v1 = pure edit-then-save:
 *
 * - No optimistic commit; local draft state accumulates changes
 * - Save button PUTs the full formulation (header + ingredient array)
 * - Delete button deletes (hard delete per D-8)
 * - Unsaved indicator shown next to Save button
 *
 * Create mode (formulationId === null): start with a blank draft, skip
 * the fetch; Save does POST instead of PUT. onCreated fires with the
 * new id so the parent can switch to edit-mode for that formulation.
 */

export interface FormulationDetailProps {
  /** null → create mode; number → edit mode for that formulation. */
  formulationId: number | null
  onDeleted?: () => void
  /** Fires after a successful POST in create mode. */
  onCreated?: (newId: number) => void
  /** Fires when the user dismisses create mode without saving. */
  onCancelCreate?: () => void
}

function blankDraft(): Formulation {
  return {
    id: 0,
    formula_code: '',
    name: '',
    batch_size_g: 100,
    notes: null,
    procedure: null,
    appearance: null,
    odor: null,
    color: null,
    ph: null,
    viscosity: null,
    customer_id: null,
    status: 'draft',
    deleted_at: null,
    created_at: null,
    updated_at: null,
    ingredients: [],
    status_history: [],
  }
}

export function FormulationDetail({
  formulationId,
  onDeleted,
  onCreated,
  onCancelCreate,
}: FormulationDetailProps) {
  const isCreateMode = formulationId === null
  const { data, isLoading, isError } = useFormulation(
    isCreateMode ? null : formulationId,
  )
  const updateMut = useUpdateFormulation()
  const createMut = useCreateFormulation()
  const deleteMut = useDeleteFormulation()
  const transitionMut = useTransitionFormulation()
  const restoreMut = useRestoreFormulation()

  // Sprint 08b — pending transition target. null = dialog closed.
  const [transitionTarget, setTransitionTarget] = useState<TransitionAction | null>(null)
  // Sprint 11 — sourcing drawer state. null = closed; otherwise the
  // ingredient whose sourcing is being viewed.
  const [sourcingFor, setSourcingFor] = useState<{
    productId: number
    label: string
  } | null>(null)

  const [draft, setDraft] = useState<Formulation | null>(
    isCreateMode ? blankDraft() : null,
  )

  // Sprint 08b — non-persistent scale-to preview. Empty = use the
  // formula's own batch_size_g (current behavior). Resets on
  // formulation switch (handled by the data effect below).
  const [scaleToInput, setScaleToInput] = useState<string>('')
  const scaleTo = useMemo<number | null>(() => {
    const trimmed = scaleToInput.trim()
    if (!trimmed) return null
    const n = Number(trimmed)
    return Number.isFinite(n) && n > 0 ? n : null
  }, [scaleToInput])

  // Reset draft whenever the server doc changes (refetch, or switch
  // formulations). Legitimate external→local state sync: react-query's
  // `data` is the source of truth; draft is a local snapshot users edit
  // until Save. Same pattern as CustomerPricelistsPage's by-partner
  // deep-link resolution.
  useEffect(() => {
    if (!data) return
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDraft(cloneFormulation(data))
    // Reset the non-persistent scale-to preview on formulation switch
    // (D-2: scale-to does NOT cross formulations).
    setScaleToInput('')
  }, [data])

  // Reset to a blank draft when switching into create mode from edit mode.
  useEffect(() => {
    if (isCreateMode) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setDraft(blankDraft())
    }
  }, [isCreateMode])

  const isDirty = useMemo(() => {
    if (!draft) return false
    if (isCreateMode) {
      // Any non-trivial field filled in = dirty.
      return (
        draft.formula_code.length > 0 ||
        draft.name.length > 0 ||
        draft.ingredients.length > 0 ||
        (draft.notes !== null && draft.notes.length > 0)
      )
    }
    if (!data) return false
    return !deepEqualFormulation(data, draft)
  }, [data, draft, isCreateMode])

  if (!isCreateMode && isError) {
    return (
      <EmptyState
        variant="error"
        title="Couldn't load formulation"
        description="The /api/formulations endpoint returned an error. Try again or pick a different formulation."
      />
    )
  }
  if (!isCreateMode && (isLoading || !draft)) {
    return (
      <div className="flex h-full items-center justify-center text-tertiary">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    )
  }
  if (!draft) {
    // Safety: should not happen — isCreateMode initializes draft in useState.
    return null
  }

  const updateHeader = (patch: Partial<Formulation>) => {
    setDraft((prev) => (prev ? { ...prev, ...patch } : prev))
  }

  const updateIngredient = (rowIndex: number, patch: Partial<Ingredient>) => {
    setDraft((prev) => {
      if (!prev) return prev
      const next = [...prev.ingredients]
      next[rowIndex] = { ...next[rowIndex], ...patch }
      return { ...prev, ingredients: next }
    })
  }

  const addIngredient = () => {
    setDraft((prev) => {
      if (!prev) return prev
      const next: Ingredient = {
        sort_order: prev.ingredients.length,
        phase: null,
        trade_name: '',
        inci_names: [],
        function: null,
        percent: 0,
        product_id: null,
        notes: null,
      }
      return { ...prev, ingredients: [...prev.ingredients, next] }
    })
  }

  const reorderIngredient = (from: number, to: number) => {
    setDraft((prev) => {
      if (!prev) return prev
      const next = [...prev.ingredients]
      const [moved] = next.splice(from, 1)
      if (!moved) return prev
      next.splice(to, 0, moved)
      return {
        ...prev,
        ingredients: next.map((ing, i) => ({ ...ing, sort_order: i })),
      }
    })
  }

  const handleSave = async () => {
    if (!draft) return
    if (!draft.formula_code.trim()) {
      toast.error('Formula code is required.')
      return
    }
    if (!draft.name.trim()) {
      toast.error('Formula name is required.')
      return
    }
    const body: FormulationWrite = toWritePayload(draft)
    try {
      if (isCreateMode) {
        const created = await createMut.mutateAsync(body)
        toast.success(`Created ${created.formula_code}`)
        onCreated?.(created.id)
      } else if (formulationId !== null) {
        await updateMut.mutateAsync({ id: formulationId, body })
        toast.success('Formulation saved')
      }
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Unknown error'
      toast.error(`Save failed: ${msg}`)
    }
  }

  const handleRevert = () => {
    if (isCreateMode) {
      setDraft(blankDraft())
      return
    }
    if (data) setDraft(cloneFormulation(data))
  }

  const handleDelete = async () => {
    if (!draft || formulationId === null) return
    // Sprint 08b: this is now a soft-delete on the backend. Surface that
    // semantically — operators can still find the formula via "Show
    // archived" + restore.
    if (
      !window.confirm(
        `Archive formulation ${draft.formula_code} — ${draft.name}?\n\n` +
          `It will move to the archived list and can be restored later.`,
      )
    )
      return
    try {
      await deleteMut.mutateAsync(formulationId)
      toast.success('Formulation archived')
      onDeleted?.()
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : 'Archive failed'
      toast.error(msg)
    }
  }

  const handleRestore = async () => {
    if (!draft || formulationId === null) return
    try {
      await restoreMut.mutateAsync(formulationId)
      toast.success('Formulation restored')
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Restore failed'
      toast.error(msg)
    }
  }

  const handleTransitionConfirm = async (comment: string | null) => {
    if (!transitionTarget || formulationId === null) return
    try {
      await transitionMut.mutateAsync({
        id: formulationId,
        body: { to: transitionTarget.to, comment },
      })
      toast.success(
        `Status: ${transitionTarget.to.charAt(0).toUpperCase() + transitionTarget.to.slice(1)}`,
      )
      setTransitionTarget(null)
    } catch (err) {
      const msg =
        err instanceof ApiError ? err.message : 'Transition failed'
      toast.error(msg)
    }
  }

  return (
    // The parent MasterDetail slot already supplies overflow-y-auto, so
    // this wrapper must NOT stack its own scroll region (doing so splits
    // the wheel into two contexts — 2026-04-24 regression). Just a plain
    // flex column; the page scroll flows through header → grid → notes.
    <div className="flex flex-col">
      {/* Header card — sticky so the code/name/Save controls stay
          visible while the user scrolls through a long ingredient grid.
          Sticks against the parent MasterDetail scroll container. */}
      <div className="sticky top-0 z-10 flex flex-col gap-3 border-b border-border-subtle bg-surface px-6 py-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div className="flex flex-wrap items-baseline gap-3">
              <input
                type="text"
                value={draft.formula_code}
                onChange={(e) => updateHeader({ formula_code: e.target.value })}
                className="min-w-[8ch] border-0 border-b border-transparent bg-transparent text-role-data-primary outline-none focus:border-accent"
                placeholder="Formula code"
              />
              <input
                type="text"
                value={draft.name}
                onChange={(e) => updateHeader({ name: e.target.value })}
                className="min-w-[20ch] flex-1 border-0 border-b border-transparent bg-transparent text-role-page-title outline-none focus:border-accent"
                placeholder="Formula name"
              />
              {/* Sprint 08b — status chip. Hidden in create mode since
                  the row doesn't exist yet (defaults to draft on save). */}
              {!isCreateMode ? (
                <StatusChip status={draft.status} />
              ) : null}
              {draft.deleted_at ? (
                <Chip size="xs" variant="neutral">
                  Archived
                </Chip>
              ) : null}
              {isDirty ? (
                <Chip size="xs" variant="warning">
                  Unsaved
                </Chip>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-secondary">
              <label className="flex items-center gap-2">
                <span>Batch size (g):</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={draft.batch_size_g}
                  onChange={(e) =>
                    updateHeader({ batch_size_g: Number(e.target.value) || 0 })
                  }
                  className="w-20 border border-border-subtle bg-surface px-2 py-0.5 text-right outline-none focus:border-accent"
                />
              </label>
              <span className="opacity-40">·</span>
              {/* Sprint 08b — scale-to preview. Empty = use formula's batch_size_g. */}
              <label className="flex items-center gap-2">
                <span>Scale to (g):</span>
                <input
                  type="number"
                  min={0}
                  step={1}
                  value={scaleToInput}
                  onChange={(e) => setScaleToInput(e.target.value)}
                  placeholder={String(draft.batch_size_g)}
                  className="w-24 border border-border-subtle bg-surface px-2 py-0.5 text-right outline-none focus:border-accent"
                />
                {scaleTo != null ? (
                  <button
                    type="button"
                    onClick={() => setScaleToInput('')}
                    aria-label="Clear scale-to preview"
                    title="Clear scale preview"
                    className="cursor-pointer rounded-xs border-none bg-transparent px-1 text-xs text-tertiary hover:text-primary"
                  >
                    ×
                  </button>
                ) : null}
              </label>
              {scaleTo != null ? (
                <span className="rounded-sm bg-info-bg px-1.5 py-0.5 text-xs text-info-text">
                  Preview only — Target (g) scaled ×
                  {(scaleTo / Number(draft.batch_size_g || 1)).toFixed(2)}
                </span>
              ) : null}
              <span className="opacity-40">·</span>
              <span className="text-tertiary">
                {isCreateMode
                  ? 'new formulation — not saved yet'
                  : `id ${draft.id}${
                      draft.updated_at
                        ? ` · updated ${new Date(draft.updated_at).toLocaleString()}`
                        : ''
                    }`}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isCreateMode ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onCancelCreate?.()}
                disabled={createMut.isPending}
              >
                <X size={14} />
                Cancel
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRevert}
                disabled={!isDirty || updateMut.isPending}
              >
                <Undo2 size={14} />
                Revert
              </Button>
            )}
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              loading={updateMut.isPending || createMut.isPending}
              disabled={!isDirty}
            >
              <Save size={14} />
              {isCreateMode ? 'Create' : 'Save'}
            </Button>
            {/* Sprint 08b — soft-delete: archive vs restore + transition buttons. */}
            {!isCreateMode && draft.deleted_at ? (
              <Button
                variant="primary"
                size="sm"
                onClick={handleRestore}
                loading={restoreMut.isPending}
                title="Restore this archived formulation"
              >
                <ArchiveRestore size={14} />
                Restore
              </Button>
            ) : !isCreateMode ? (
              <Button
                variant="danger"
                size="sm"
                iconOnly
                onClick={handleDelete}
                loading={deleteMut.isPending}
                title="Archive (soft-delete) — can be restored later"
                aria-label="Archive formulation"
              >
                <Trash2 size={14} />
              </Button>
            ) : null}
          </div>
        </div>
        {!isCreateMode && !draft.deleted_at ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-border-subtle pt-3">
            <span className="text-role-section-label text-secondary">Workflow</span>
            {transitionsFor(draft.status).map((action) => (
              <Button
                key={action.to}
                variant={action.variant}
                size="sm"
                onClick={() => setTransitionTarget(action)}
                disabled={isDirty || transitionMut.isPending}
                title={
                  isDirty
                    ? 'Save changes before transitioning'
                    : action.label
                }
              >
                <action.icon size={14} />
                {action.label}
              </Button>
            ))}
            {isDirty ? (
              <span className="text-role-metadata text-tertiary">
                Save changes to enable workflow transitions.
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Grid — renders to content height (not its own scroll region) so
          the outer column scrolls as one piece and Notes / Procedure land
          directly below without a second scrollbar to fight. */}
      <div className="p-4">
        <IngredientsGrid
          ingredients={draft.ingredients}
          batchSizeG={Number(draft.batch_size_g)}
          scaleTo={scaleTo}
          onIngredientChange={updateIngredient}
          onAddRow={addIngredient}
          onRowReorder={reorderIngredient}
          onShowSourcing={(ing) => {
            if (ing.product_id) {
              setSourcingFor({
                productId: ing.product_id,
                label: ing.trade_name || `Product #${ing.product_id}`,
              })
            }
          }}
        />
      </div>

      {/* Notes + procedure footer */}
      <div className="flex flex-col gap-4 border-t border-border-subtle bg-surface-sunk px-6 py-4">
        <section>
          <h3 className="text-role-section-label mb-1">Notes</h3>
          <textarea
            value={draft.notes ?? ''}
            onChange={(e) => updateHeader({ notes: e.target.value || null })}
            rows={2}
            className="w-full border border-border-subtle bg-surface p-2 text-sm outline-none focus:border-accent"
            placeholder="Free-form formulation notes…"
          />
        </section>
        <section>
          <h3
            className="text-role-section-label mb-1"
            title="Drag to reorder. Step numbers auto-renumber on add/remove."
          >
            Procedure
          </h3>
          <ProcedureEditor
            procedure={draft.procedure ?? []}
            onChange={(next) =>
              updateHeader({
                procedure: next.length > 0 ? next : null,
              })
            }
            readOnly={Boolean(draft.deleted_at)}
          />
        </section>
        <section>
          <h3 className="text-role-section-label mb-1">Specifications</h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(
              [
                ['appearance', 'Appearance', 'e.g. Clear amber liquid'],
                ['odor', 'Odor', 'e.g. Characteristic'],
                ['color', 'Color', 'e.g. APHA <50'],
                ['ph', 'pH', 'e.g. 5.5 or 5.5–7.0'],
                ['viscosity', 'Viscosity', 'e.g. 6,000 cps'],
              ] as Array<
                [
                  'appearance' | 'odor' | 'color' | 'ph' | 'viscosity',
                  string,
                  string,
                ]
              >
            ).map(([key, label, placeholder]) => (
              <label key={key} className="flex flex-col gap-1">
                <span className="text-role-metadata">{label}</span>
                <input
                  type="text"
                  value={draft[key] ?? ''}
                  onChange={(e) =>
                    updateHeader({ [key]: e.target.value || null })
                  }
                  placeholder={placeholder}
                  className="border border-border-subtle bg-surface p-2 text-sm outline-none focus:border-accent"
                  readOnly={Boolean(draft.deleted_at)}
                />
              </label>
            ))}
          </div>
        </section>
        {!isCreateMode ? (
          <HistoryPanel history={draft.status_history ?? []} />
        ) : null}
      </div>
      {/* Sprint 08b — confirm dialog for state-machine transitions. */}
      <TransitionDialog
        open={transitionTarget !== null}
        target={transitionTarget?.to ?? 'draft'}
        label={transitionTarget?.label ?? ''}
        confirmVariant={transitionTarget?.variant ?? 'primary'}
        onConfirm={handleTransitionConfirm}
        onCancel={() => setTransitionTarget(null)}
        pending={transitionMut.isPending}
      />
      {/* Sprint 11 — sourcing drawer per ingredient. */}
      <Drawer
        open={sourcingFor !== null}
        onOpenChange={(open) => {
          if (!open) setSourcingFor(null)
        }}
        title="Sourcing"
        meta={sourcingFor?.label ?? ''}
      >
        <SourcingPanel
          productId={sourcingFor?.productId ?? null}
          productLabel={sourcingFor?.label ?? null}
        />
      </Drawer>
    </div>
  )
}

// ─── helpers ─────────────────────────────────────────────────────────

function cloneFormulation(f: Formulation): Formulation {
  return {
    ...f,
    ingredients: f.ingredients.map((i) => ({
      ...i,
      inci_names: [...(i.inci_names ?? [])],
    })),
    procedure: f.procedure ? f.procedure.map((s) => ({ ...s })) : null,
  }
}

function deepEqualFormulation(a: Formulation, b: Formulation): boolean {
  if (a.formula_code !== b.formula_code) return false
  if (a.name !== b.name) return false
  if (Number(a.batch_size_g) !== Number(b.batch_size_g)) return false
  if ((a.notes ?? '') !== (b.notes ?? '')) return false
  if (a.customer_id !== b.customer_id) return false
  if (a.ingredients.length !== b.ingredients.length) return false
  for (let i = 0; i < a.ingredients.length; i++) {
    const ai = a.ingredients[i]
    const bi = b.ingredients[i]
    if (!ai || !bi) return false
    if (ai.sort_order !== bi.sort_order) return false
    if ((ai.phase ?? '') !== (bi.phase ?? '')) return false
    if (ai.trade_name !== bi.trade_name) return false
    if ((ai.function ?? '') !== (bi.function ?? '')) return false
    if (ai.percent !== bi.percent) return false
    if (ai.product_id !== bi.product_id) return false
    if ((ai.notes ?? '') !== (bi.notes ?? '')) return false
    const aInci = ai.inci_names ?? []
    const bInci = bi.inci_names ?? []
    if (aInci.length !== bInci.length) return false
    for (let j = 0; j < aInci.length; j++) {
      if (aInci[j] !== bInci[j]) return false
    }
  }
  // Procedure (Sprint 08b — newly editable). Compare step text and
  // length; the displayed step number is auto-rendered so we don't need
  // to compare it.
  const aProc = a.procedure ?? []
  const bProc = b.procedure ?? []
  if (aProc.length !== bProc.length) return false
  for (let i = 0; i < aProc.length; i++) {
    const ap = aProc[i]
    const bp = bProc[i]
    if (!ap || !bp) return false
    if ((ap.text ?? '') !== (bp.text ?? '')) return false
  }
  return true
}

function toWritePayload(f: Formulation): FormulationWrite {
  return {
    formula_code: f.formula_code,
    name: f.name,
    batch_size_g: Number(f.batch_size_g),
    notes: f.notes,
    procedure: f.procedure,
    customer_id: f.customer_id,
    appearance: f.appearance,
    odor: f.odor,
    color: f.color,
    ph: f.ph,
    viscosity: f.viscosity,
    ingredients: f.ingredients.map((i) => ({
      sort_order: i.sort_order,
      phase: i.phase,
      trade_name: i.trade_name,
      inci_names: i.inci_names && i.inci_names.length > 0 ? i.inci_names : null,
      function: i.function,
      percent: i.percent,
      product_id: i.product_id,
      notes: i.notes,
    })),
  }
}
